/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { mutate } from 'swr';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ErrorWithInfo, useMutate } from '@/net/commons';

const formSchema = z.object({
    email: z
        .string()
        .min(3, { message: 'Email is too short' })
        .max(100, { message: 'Email is too long' })
        .email({ message: 'Email is invalid' }),
});

export default function AddMemberDialog({ onInvite }: { onInvite: () => void }) {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const form = useForm<z.infer<typeof formSchema>>({
        defaultValues: {
            email: '',
        },
        resolver: zodResolver(formSchema),
    });
    const { trigger } = useMutate(`/bff/organisation/invitations`, {
        method: 'POST',
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            setIsLoading(true);
            await trigger(values);
            mutate((key) => typeof key === 'string' && key.startsWith('/bff/organisation/invitations'), undefined, {
                revalidate: true,
            });
            setOpen(false);
            onInvite();
        } catch (err) {
            setIsLoading(false);
            const message = (err as ErrorWithInfo)?.info?.error ?? 'There was a problem with your request';
            form.setError('email', { message });
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                form.reset();
                setOpen(o);
                setIsLoading(false);
            }}
        >
            <DialogTrigger asChild>
                <Button className="w-max">Add Member</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Invite New Member</DialogTitle>
                    <DialogDescription>
                        If an invitation is sent, the user will receive an email with a link to join your organisation.
                        This invite will expire in 30 days.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex flex-row gap-2 items-center">Email</FormLabel>
                                    <FormControl>
                                        <Input placeholder="john@example.com" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex flex-row justify-end">
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? 'Inviting...' : 'Invite'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
